import React, { useState, useEffect, useMemo } from "react";
import { PolyIcon } from "elements";
import { LocalKeys, useTranslation } from "utilities/i18n";
import {
  SoftwareUpdateStageOptions,
  SoftwareUpdateStatus,
  SoftwareUpdateStatusOptions,
  useOsStateQuery
} from "generated/graphql";
import { DISMISS, useNotification } from "collections/Notifications";

const UpdateProgress: React.FC<{
  id: string;
  updating: boolean;
  setUpdating: (updating: boolean) => void;
}> = ({ id, updating, setUpdating }) => {
  const { t } = useTranslation();
  const [stage, setStage] = useState<LocalKeys>("UPDATING");
  const [likelyRebooting, setLikely] = useState(false);
  const [minComplete, setComplete] = useState(0);
  const [offline, setOffline] = useState(false);
  const [addNotification] = useNotification();
  const [updateDownloaded, setDownloaded] = useState(false);
  const [interval, setInterval] = useState(updating ? 1000 : undefined);

  const { fetching, data } = useOsStateQuery({
    variables: {
      filter: {
        AND: [
          {
            field: "id",
            eq: id
          }
        ]
      }
    },
    requestPolicy: "network-only",
    pollInterval: interval
  });
  let status = useMemo(() => {}, []) as SoftwareUpdateStatus | null | undefined;

  if (!fetching && data && data.devices && data.devices.length) {
    const device = data.devices[0];
    status = device.currentOSState?.updateStatus;
    if (
      device.connected &&
      offline &&
      updating &&
      likelyRebooting &&
      minComplete > 90
    ) {
      setUpdating(false);
      setInterval(undefined);
      addNotification({
        id: "device-os-update-success",
        type: "success",
        message: t("DEVICE_OS_UPDATE_SUCCESS"),
        dismiss: DISMISS.DEFAULT
      });
    } else if (offline === device.connected) {
      setOffline(!device.connected);
    }
  }

  useEffect(() => {
    const {
      DownloadingPackage,
      Installing,
      Authenticating
    } = SoftwareUpdateStageOptions;
    if (status) {
      const {
        softUpdateStage: stage,
        progressState: state,
        percentageComplete: percent
      } = status;

      const updatingStates: Array<String> = [
        "MODULE_UPDATING",
        "LOCAL_UPDATING"
      ];
      const updatingStages: Array<SoftwareUpdateStageOptions> = [
        DownloadingPackage,
        Installing,
        Authenticating
      ];

      if (percent && percent > minComplete) {
        setComplete(percent);
      }
      if (
        ((stage && updatingStages.includes(stage)) ||
          (state && updatingStates.includes(state))) &&
        !updating
      ) {
        setUpdating(true);
        setInterval(10000);
      } else if (updating && (offline || minComplete > 75)) {
        setLikely(true);
        if (!interval || interval < 15000) {
          setInterval(15000);
        }
      } else if (stage === Installing) {
        setDownloaded(true);
      } else if (
        state === "IDLE" &&
        status.softUpdateStatus === SoftwareUpdateStatusOptions.NoUpdate &&
        updating &&
        updateDownloaded
      ) {
        setUpdating(false);
        setInterval(undefined);
        addNotification({
          id: "device-os-update-success",
          type: "success",
          message: t("DEVICE_OS_UPDATE_SUCCESS"),
          dismiss: DISMISS.DEFAULT
        });
      }
    }
  }, [
    fetching,
    data,
    status,
    setUpdating,
    setInterval,
    updating,
    interval,
    offline,
    updateDownloaded,
    minComplete,
    addNotification,
    t
  ]);

  if (status?.softUpdateStage && status.softUpdateStage !== stage) {
    const statusKey: LocalKeys = `${status.softUpdateStage ||
      "UPDATING"}` as const;
    setStage(statusKey);
  }

  let content = t(stage);
  if (status === null && (offline || likelyRebooting)) {
    content = t("REBOOTING");
  }
  return updating ? (
    <div className="sw-update block" data-testid="update-modal">
      <PolyIcon icon="refresh" className="refresh spin inline-block" />
      <div className="indicator inline-block">
        <div className="complete" style={{ width: `${minComplete}%` }} />
        <div className="info p-0" data-testid="progress-content">
          {content}
          <span className="percentage pl-1">
            {likelyRebooting && status === null ? "" : `${minComplete}%`}
          </span>
        </div>
      </div>
    </div>
  ) : null;
};

export default UpdateProgress;
